import {Button, Group, Select, Stack, TagsInput, TextInput, Title} from "@mantine/core";
import {IconClipboardText, IconPlus} from "@tabler/icons-react";
import {useCallback, useState} from "react";
import {toHiragana} from "wanakana";
import type {LibraryEntry, Reading} from "@/shared/types";
import {type BatchRow, DEFAULT_SRS_STATE} from "../types";
import {BatchEntryPasteArea} from "./BatchEntryPasteArea";
import {BatchEntryTable} from "./BatchEntryTable";

const createEmptyRow = (): BatchRow => ({
  id: crypto.randomUUID(),
  expression: "",
  field1: "",
  field2: "",
  meaning: "",
  auxiliary: "",
});

/**
 * BatchEntryForm allows the user to input multiple library entries at once.
 * It provides a spreadsheet-like interface for editing and a batch-paste area for quick import.
 */
export function BatchEntryForm(props: {
  onSubmit: (entries: Omit<LibraryEntry, "createdAt" | "updatedAt">[]) => void;
  loading?: boolean;
}) {
  const {onSubmit, loading} = props;

  const [type, setType] = useState<"vocab" | "character">("vocab");
  const [languageCode, setLanguageCode] = useState("ja");
  const [rows, setRows] = useState<BatchRow[]>([createEmptyRow()]);
  const [grouping, setGrouping] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [showPaste, setShowPaste] = useState(false);

  const handleBatchSubmit = useCallback(
    (e: React.SubmitEvent<HTMLFormElement>) => {
      e.preventDefault();
      const validEntries = rows
        .filter(row => row.expression.trim() !== "" && row.meaning.trim() !== "")
        .map((row): Omit<LibraryEntry, "createdAt" | "updatedAt"> => {
          const readings: Reading[] = [];

          if (languageCode === "ja") {
            if (type === "vocab") {
              const expression = row.expression.trim();
              const romajiInput = row.field1.trim();

              if (romajiInput) {
                const kana = toHiragana(romajiInput);
                readings.push({type: "romaji", value: romajiInput});
                readings.push({type: "kana", value: kana});
              } else if (expression) {
                const kana = toHiragana(expression);
                const hasKanji = kana !== expression;
                if (hasKanji) {
                  readings.push({type: "kana", value: kana});
                }
              }
            } else {
              if (row.field1) {
                readings.push({type: "on", value: row.field1});
              }
              if (row.field2) {
                readings.push({type: "kun", value: row.field2});
              }
            }
          } else if (languageCode === "zh") {
            if (row.field1) {
              readings.push({type: "pinyin", value: row.field1});
            }
          }

          return {
            type,
            languageCode,
            grouping: grouping || undefined,
            tags: tags.length > 0 ? tags : undefined,
            content: {
              expression: row.expression.trim(),
              meaning: row.meaning.trim(),
              readings: readings.length > 0 ? readings : undefined,
              auxiliary: row.auxiliary.trim() || undefined,
            },
            srsState: DEFAULT_SRS_STATE,
          };
        });

      if (validEntries.length > 0) {
        onSubmit(validEntries);
      }
    },
    [rows, type, grouping, tags, languageCode, onSubmit],
  );

  const updateRow = (
    id: string,
    field: keyof BatchRow | Partial<BatchRow>,
    value?: string,
  ) => {
    if (typeof field === "object") {
      setRows(rows.map(row => (row.id === id ? {...row, ...field} : row)));
    } else {
      setRows(rows.map(row => (row.id === id ? {...row, [field]: value} : row)));
    }
  };

  const addRow = () => {
    setRows([...rows, createEmptyRow()]);
  };

  const removeRow = (id: string) => {
    const newRows = rows.filter(r => r.id !== id);
    if (newRows.length === 0) {
      setRows([createEmptyRow()]);
    } else {
      setRows(newRows);
    }
  };

  const handleProcessPaste = (newEntries: BatchRow[]) => {
    const currentRows = rows.filter(
      r => r.expression.trim() !== "" || r.meaning.trim() !== "",
    );
    setRows([...currentRows, ...newEntries]);
    setShowPaste(false);
  };

  return (
    <Stack>
      <Title order={4}>Batch Import</Title>
      <Stack gap="sm">
        <Group grow align="flex-end">
          <Select
            label="Entry Type"
            value={type}
            onChange={val => setType((val as "vocab" | "character") || "vocab")}
            data={[
              {label: "Vocabulary", value: "vocab"},
              {label: "Character/Kanji", value: "character"},
            ]}
            allowDeselect={false}
            required
          />
          <Select
            label="Language Code"
            value={languageCode}
            onChange={val => setLanguageCode(val || "ja")}
            data={[
              {value: "ja", label: "Japanese (ja)"},
              {value: "zh", label: "Chinese (zh)"},
              {value: "en", label: "English (en)"},
            ]}
            allowDeselect={false}
            required
          />
        </Group>
        <Group grow align="flex-end">
          <TextInput
            label="Grouping (Class/Book)"
            placeholder="e.g. Clase 4"
            value={grouping}
            onChange={e => setGrouping(e.currentTarget.value)}
          />
          <TagsInput
            label="Tags"
            placeholder="Pick tags"
            value={tags}
            onChange={setTags}
          />
        </Group>
      </Stack>

      <form onSubmit={handleBatchSubmit}>
        <Stack>
          <BatchEntryTable
            rows={rows}
            type={type}
            languageCode={languageCode}
            onUpdateRow={updateRow}
            onRemoveRow={removeRow}
          />

          <Group justify="space-between">
            <Group>
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={addRow}
              >
                Add Row
              </Button>
              <Button
                variant="subtle"
                leftSection={<IconClipboardText size={16} />}
                onClick={() => setShowPaste(!showPaste)}
              >
                {showPaste ? "Hide Paste Area" : "Paste from Clipboard"}
              </Button>
            </Group>
            <Button
              type="submit"
              loading={loading}
              disabled={
                rows.filter(r => r.expression.trim() && r.meaning.trim()).length === 0
              }
            >
              Import Batch
            </Button>
          </Group>
        </Stack>
      </form>

      {showPaste && (
        <BatchEntryPasteArea
          languageCode={languageCode}
          type={type}
          onProcessPaste={handleProcessPaste}
        />
      )}
    </Stack>
  );
}
